<template>
  <div class="kt-grid kt-grid--ver kt-grid--root">
    <div
      class="kt-grid kt-grid--hor kt-grid--root kt-login kt-login--v6 kt-login--signin"
      id="kt_login"
    >
      <div class="auth-topbar">
        <router-link :to="{ name: 'login' }" class="auth-topbar__logo">
          <img :src="site.meta.default.logo" alt="Studio Metrodesk" />
        </router-link>
      </div>

      <div
        class="kt-grid__item kt-grid__item--fluid kt-grid kt-grid--desktop kt-grid--ver-desktop kt-grid--hor-tablet-and-mobile"
      >
        <div
          class="kt-grid__item kt-grid__item--order-tablet-and-mobile-2 kt-grid kt-grid--hor kt-login__aside"
        >
          <div class="kt-login__wrapper">
            <div class="kt-login__container auth-form">
              <div class="kt-login__body">
                <GuestUpdatePassword
                  v-if="orderSummary"
                  :order-summary="orderSummary"
                />

                <template v-if="!loadingOrderSummary && !orderSummary">
                  <!-- ── Step 1: Login form ───────────────────────────────── -->
                  <div
                    v-if="loginForm"
                    class="kt-login__signin col-sm-12 col-md-12 col-lg-10 col-xl-8 col-12 mx-auto"
                  >
                    <div class="kt-head px-0 text-center mb-0">
                      <h3 class="kt-title" style="font-size:1.8rem; font-weight:700; color:#1a1a2e;">
                        Welcome Back
                      </h3>
                      <p class="text-muted mt-2" style="font-size:0.95rem;">
                        Log in to manage your orders, track progress, and download your completed images.
                      </p>
                    </div>

                    <Alert v-if="successMessage" variant="success">
                      {{ successMessage }}
                    </Alert>
                    <Alert v-if="errorMessage" variant="danger">
                      {{ errorMessage }}
                    </Alert>

                    <!-- Social sign-in buttons -->
                    <SocialSignInButtons
                      @success="handleSocialSignIn"
                      @error="handleSocialError"
                    />

                    <!-- Divider -->
                    <div class="auth-divider my-3">
                      <span>Or Log in through email</span>
                    </div>

                    <form class="kt-form" @submit.prevent="submit">
                      <div class="form-group">
                        <input
                          class="form-control"
                          type="email"
                          placeholder="Enter your email"
                          v-model="form.email"
                          required
                        />
                      </div>
                      <div class="form-group">
                        <div class="password-field">
                          <input
                            class="form-control"
                            :type="showPassword ? 'text' : 'password'"
                            placeholder="Password"
                            v-model="form.password"
                            required
                          />
                          <button
                            type="button"
                            class="password-toggle"
                            tabindex="-1"
                            @click="showPassword = !showPassword"
                          >
                            <i :class="showPassword ? 'la la-eye-slash' : 'la la-eye'"></i>
                          </button>
                        </div>
                        <a
                          class="float-right text-primary mt-1"
                          style="font-size:0.85rem;"
                          href="#"
                          @click.prevent="startResetPasswordProcess"
                        >Forgot password?</a>
                      </div>
                      <div class="kt-login__extra form-group">
                        <label class="kt-checkbox">
                          <input type="checkbox" v-model="form.remember" />
                          Remember me
                          <span></span>
                        </label>
                      </div>
                      <div class="form-group">
                        <button
                          type="submit"
                          class="btn btn-brand w-100 btn-elevate"
                          :disabled="busy"
                        >
                          <div class="d-flex justify-content-center align-items-center">
                            <span>Login</span>
                            <div
                              v-if="busy"
                              class="spinner-border spinner-border-sm ml-2"
                              role="status"
                            >
                              <span class="sr-only">Loading...</span>
                            </div>
                          </div>
                        </button>
                      </div>
                    </form>

                    <div class="kt-login__account text-center" style="font-size:1rem;">
                      <span class="kt-login__account-msg">Don't have an account?</span>
                      <router-link
                        :to="{ name: 'register' }"
                        class="font-weight-bold mx-2 kt-login__account-link"
                        style="font-size: inherit"
                      >Sign up</router-link>
                    </div>
                  </div>

                  <!-- ── Step 2: Enter email for password reset ──────────── -->
                  <div
                    v-if="secondStep"
                    class="kt-login__register col-sm-12 col-md-12 col-lg-10 col-xl-8 col-12 mx-auto"
                  >
                    <div>
                      <h3 class="kt-title">Reset your password</h3>
                    </div>
                    <div class="form-group">
                      Please enter your email address. We will send you a
                      verification code to the email address.
                    </div>
                    <div>
                      <div class="form-group">
                        <div class="row">
                          <div class="col">
                            <input
                              v-model="verification.email"
                              class="form-control"
                              placeholder="Enter Your Email"
                              type="text"
                            />
                            <span
                              v-if="verification.emailErrorMessage"
                              class="form-text text-danger"
                            >{{ verification.emailErrorMessage }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="form-group">
                        <button
                          :disabled="busy"
                          class="btn btn-brand w-100 btn-elevate"
                          type="submit"
                          @click="submitEmailForVerification"
                        >
                          <span>Submit</span>
                          <span
                            v-if="busy"
                            class="spinner-border spinner-border-sm ml-2"
                            role="status"
                          ></span>
                        </button>
                      </div>
                      <a class="text-wrap mt-3" href="#" @click.prevent="backToLogin">Back to Log in page</a>
                    </div>
                  </div>

                  <!-- ── Step 3: Enter verification code ───────────────── -->
                  <div
                    v-if="thirdStep"
                    class="kt-login__register col-sm-12 col-md-12 col-lg-10 col-xl-8 col-12 mx-auto"
                  >
                    <div>
                      <h3 class="kt-title">Confirm your email</h3>
                    </div>
                    <div>
                      <div class="form-group">
                        <div class="row">
                          <div class="col">
                            <input
                              v-model="verification.verification_code"
                              class="form-control"
                              placeholder="Code"
                              type="text"
                            />
                            <span
                              v-if="verification.codeErrorMessage"
                              class="form-text text-danger"
                            >{{ verification.codeErrorMessage }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="form-group">
                        <button
                          class="btn btn-brand w-100 btn-elevate"
                          type="submit"
                          @click="verify"
                        >
                          <span>Verify</span>
                        </button>
                      </div>
                      <a class="text-wrap mt-3" href="#" @click.prevent="backToLogin">Back to Log in page</a>
                    </div>
                  </div>

                  <!-- ── Step 4: Set new password ───────────────────────── -->
                  <div
                    v-if="fourthStep"
                    class="kt-login__register col-sm-12 col-md-12 col-lg-10 col-xl-8 col-12 mx-auto"
                  >
                    <div>
                      <h3 class="kt-title">Set New password</h3>
                    </div>
                    <div class="form-group">
                      Please, enter your new password twice, so we can verify you typed it correctly.
                    </div>
                    <div>
                      <div class="form-group">
                        <input
                          v-model="verification.password"
                          class="form-control"
                          placeholder="Password"
                          type="password"
                        />
                      </div>
                      <div class="form-group">
                        <input
                          v-model="verification.confirmPassword"
                          class="form-control"
                          placeholder="Confirm Password"
                          type="password"
                        />
                        <span
                          v-if="verification.confirmPasswordError"
                          class="form-text text-danger"
                        >{{ verification.confirmPasswordError }}</span>
                      </div>
                      <div class="form-group">
                        <button
                          :disabled="busy"
                          class="btn btn-brand w-100 btn-elevate"
                          type="submit"
                          @click="updatePassword"
                        >
                          <span>Reset Password</span>
                          <span
                            v-if="busy"
                            class="spinner-border spinner-border-sm ml-2"
                            role="status"
                          ></span>
                        </button>
                      </div>
                      <a class="text-wrap mt-3" href="#" @click.prevent="backToLogin">Back to Log in page</a>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Right hero panel -->
        <div
          :style="{ backgroundImage: `url(${this.banner})` }"
          class="kt-grid__item kt-grid__item--fluid kt-grid__item--center kt-grid kt-grid--ver kt-login__content"
        >
          <div class="kt-login__section">
            <div class="kt-login__block mx-5">
              <h3 class="kt-login__title">
                {{ site.meta.login.hero_section_title }}
              </h3>
              <div class="kt-login__desc">
                {{ site.meta.login.hero_section_description }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import Alert from '@/components/Alert.vue';
import GuestUpdatePassword from '@/components/GuestUpdatePassword.vue';
import SocialSignInButtons from '@/components/SocialSignInButtons.vue';
import { decrypt } from '@/helpers/support.js';

export default {
  components: {
    Alert,
    GuestUpdatePassword,
    SocialSignInButtons,
  },

  data() {
    return {
      banner: '',
      showPassword: false,
      form: {
        email: '',
        password: '',
        remember: true,
      },
      errors: {},
      successMessage: '',
      errorMessage: '',
      busy: false,
      verification: {
        email: '',
        emailErrorMessage: '',
        verification_code: '',
        codeErrorMessage: '',
        password: '',
        confirmPassword: '',
        confirmPasswordError: '',
      },
      loginForm: true,
      secondStep: false,
      thirdStep: false,
      fourthStep: false,
      loadingOrderSummary: false,
      orderSummary: null,
    };
  },

  computed: {
    ...mapGetters({
      site: 'site/site',
    }),
  },

  created() {
    if (this.$route.query?.ref) {
      const orderID = decrypt(this.$route.query?.ref);
      if (orderID) {
        this.loadingOrderSummary = true;
        axios
          .get('guest-order-summary/' + orderID)
          .then((response) => {
            if (
              response?.data?.user?.registration_method &&
              !response.data.user.last_login
            ) {
              this.orderSummary = response.data;
            }
          })
          .finally(() => {
            this.loadingOrderSummary = false;
          });
      }
    }
  },

  mounted() {
    this.banner = this.site.meta.login.banner;
    this.setPageMeta(this.site.meta.login);

    if (this.$route.query?.success === 'account-created') {
      this.successMessage = 'Account created successfully!';
    }
  },

  methods: {
    ...mapActions({
      login: 'auth/login',
      socialSignIn: 'auth/socialSignIn',
      sendVerificationCode: 'auth/sendCodeForResetPassword',
      verifyCode: 'auth/verify',
      resetPassword: 'auth/resetPassword',
    }),

    setPageMeta(meta = {}) {
      const { page_title, page_description, robots_index } = meta;
      if (page_title) document.title = page_title || 'Studio Metrodesk';
      const descEl = document.querySelector('meta[name="description"]');
      if (descEl) descEl.setAttribute('content', page_description || '');
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.setAttribute('content', robots_index === 'noindex' ? 'noindex, nofollow' : 'index, follow');
    },

    submit() {
      if (this.busy) return;
      this.busy = true;
      this.errorMessage = '';

      this.login({ credential: this.form, remember: this.form.remember })
        .then(() => {
          this.$router.replace({ name: 'home' });
        })
        .catch((error) => {
          this.errors = error.response.data.errors || {};
          this.errorMessage = error.response.data.message;
        })
        .finally(() => {
          this.busy = false;
        });
    },

    handleSocialSignIn({ provider, providerToken, firstName, lastName }) {
      if (this.busy) return;
      this.busy = true;
      this.errorMessage = '';

      this.socialSignIn({ provider, providerToken, firstName, lastName })
        .then(() => {
          this.$router.replace({ name: 'home' });
        })
        .catch((error) => {
          const status = error?.response?.status;
          if (status === 422) {
            this.errorMessage = 'Invalid or expired token. Please try again.';
          } else if (status === 419) {
            this.errorMessage = 'Your account has been locked. Please contact support.';
          } else if (status === 403) {
            this.errorMessage = 'Access denied for this site.';
          } else {
            this.errorMessage = 'Something went wrong. Please try again.';
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },

    handleSocialError({ message }) {
      this.errorMessage = message;
    },

    startResetPasswordProcess() {
      this.loginForm = false;
      this.banner = this.site.meta.login.second_banner;
      this.secondStep = true;
      this.thirdStep = false;
      this.fourthStep = false;
    },

    backToLogin() {
      this.busy = false;
      this.loginForm = true;
      this.banner = this.site.meta.login.banner;
      this.secondStep = false;
      this.thirdStep = false;
      this.fourthStep = false;
    },

    submitEmailForVerification() {
      this.busy = true;

      this.sendVerificationCode(this.verification).then((data) => {
        this.busy = false;

        if (data.data.success) {
          this.loginForm = false;
          this.secondStep = false;
          this.thirdStep = true;
          this.fourthStep = false;
        } else {
          this.verification.emailErrorMessage = 'We did not find your email, please sign in.';
        }
      });
    },

    verify() {
      this.verifyCode(this.verification).then((data) => {
        if (data.data.success) {
          this.loginForm = false;
          this.secondStep = false;
          this.thirdStep = false;
          this.fourthStep = true;
        } else {
          this.verification.codeErrorMessage = 'Verification code mismatched';
          this.busy = false;
        }
      });
    },

    updatePassword() {
      if (this.verification.password !== this.verification.confirmPassword) {
        this.verification.confirmPasswordError = 'The password confirmation does not match.';
        return;
      }

      if (this.verification.password.length < 8) {
        this.verification.confirmPasswordError = 'The password must be at least 8 characters.';
        return;
      }

      this.busy = true;

      this.resetPassword(this.verification).then((data) => {
        if (data.data.success) {
          this.busy = false;
          this.form.email = this.verification.email;
          this.form.password = this.verification.confirmPassword;
          this.submit();
        } else {
          this.verification.confirmPasswordError = 'Verification failed! Please try again.';
        }
      });
    },
  },
};
</script>

<style src="../../assets/css/auth.css"></style>
<style>
.kt-login__signin .kt-head .kt-title:before,
.kt-login__signin .kt-head .kt-title:after {
  background: none;
}

.auth-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #9ca3af;
  font-size: 0.85rem;
}
.auth-divider::before,
.auth-divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #e5e7eb;
}
.auth-divider::before { margin-right: 0.75em; }
.auth-divider::after  { margin-left:  0.75em; }

.password-field {
  position: relative;
}
.password-field .form-control {
  padding-right: 2.5rem;
}
.password-toggle {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 0;
  line-height: 1;
}
.password-toggle:hover { color: #6b7280; }
</style>
